import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Batch, CreateOrderChargePayload, OrderPaymentIntent, UserProfile } from '../constants';
import { PaymentMethod } from '@stripe/stripe-js';

@Injectable({
  providedIn: 'root'
})
export class FirebaseFunctionsService {
    private firebaseFunctions: Functions = inject(Functions);
    
    constructor() {}

    async getUserLoginCustomToken(uid: string): Promise<{token: string}> {
        const createLoginCustomTokenFn = httpsCallable(this.firebaseFunctions, 'createLoginCustomToken');
        try {
            const response = await createLoginCustomTokenFn({idToken: uid});
            const data = response.data as {token: string};
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return {token: ''};
        }
    }

    async getProductsDataFromTextPrompt(text: string, userId: string) {
        const productsData = httpsCallable(this.firebaseFunctions, 'convertTextToData');
        try {
            const response = await productsData({userPrompt: text, userId });
            const data = response.data as string;
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return '';
        }
    }

    async calculateDeliveryRoute(storeNames: string[], deliveryAddress: string) {
        const calculateDeliveryRouteFn = httpsCallable(this.firebaseFunctions, 'calculateDeliveryRoute');
        try {
            const response = await calculateDeliveryRouteFn({ storeNames, deliveryAddress});
            const data = response.data as string;
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return '';
        }
    }

    async calculateRouteMetrics(storeNames: string[], deliveryAddress: string | {lat: number, lng: number}, thresholdKm?: number, perKmRate?: number): Promise<{
        storeLocations: {name: string, address: string, lat: number, lng: number}[], 
        totalDistanceMiles: number, 
        extraFee: number, 
        deliveryLocation: {lat: number, lng: number} | null, 
        routeLegs: {startAddress: string, endAddress: string, distanceMiles: number, durationMins: number}[]}> {
        const calculateRouteMetricsFn = httpsCallable(this.firebaseFunctions, 'calculateRouteMetrics');
        try {
            console.log({ storeNames, deliveryAddress, thresholdKm, perKmRate });
            const response = await calculateRouteMetricsFn({ storeNames, deliveryAddress, thresholdKm, perKmRate });
            const data = response.data as {storeLocations: {name: string, address: string, lat: number, lng: number}[], totalDistanceMiles: number, extraFee: number, deliveryLocation: {lat: number, lng: number} | null, routeLegs: {startAddress: string, endAddress: string, distanceMiles: number, durationMins: number}[]};
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return {storeLocations: [], totalDistanceMiles: 0, extraFee: 0, deliveryLocation: null, routeLegs: []};
        }
    }

    async createGroceryListFromHistory(userProfile: UserProfile) {
        const createShoppingListFromHistoryFn = httpsCallable(this.firebaseFunctions, 'createShoppingListFromHistory');
        try {
            await createShoppingListFromHistoryFn({...userProfile});
            return true;
        } catch (error: unknown) {
            console.log(error);
            return false;
        }
    }

    async createStripeCustomer(userProfile: UserProfile) {
        const createStripeCustomerFn = httpsCallable(this.firebaseFunctions, 'createStripeCustomer');
        try {
            const response = await createStripeCustomerFn({...userProfile});
            const data = response.data;
            console.log(data);
        } catch (error: unknown) {
            console.log(error);
        }
    }

    async createStripePaymentSetupIntent({stripeCustomerId}: Partial<UserProfile>): Promise<{paymentSetupIntentClientSecret: string; customerSessionClientSecret: string} | null> {
        const createStripePaymentSetupIntentFn = httpsCallable(this.firebaseFunctions, 'createPaymentSetupIntentAndCustomerSession');
        try {
            const response = await createStripePaymentSetupIntentFn({stripeCustomerId});
            const data = response.data as {paymentSetupIntentClientSecret: string; customerSessionClientSecret: string};
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return null;
        }
    }

    async getCustomerPaymentMethods({stripeCustomerId}: Partial<UserProfile>): Promise<Array<PaymentMethod>> {
        const getCustomerPaymentMethodsFn = httpsCallable(this.firebaseFunctions, 'getCustomerPaymentMethods');
        try {
            const response = await getCustomerPaymentMethodsFn({stripeCustomerId}) as {data: { data: Array<PaymentMethod>}};
            const data = response.data.data as Array<PaymentMethod>;
            return data;
        } catch (error: unknown) {
            console.log(error);
            return [];
        }
    }

    async createChargeOffSession({
            stripeCustomerId,
            amount,
            currency,
            paymentMethodId,
            returnUrl
        }: CreateOrderChargePayload
    ): Promise<OrderPaymentIntent | null> {
        const createChargeOffSessionFn = httpsCallable(this.firebaseFunctions, 'createChargeOffSession');
        try {
            const response = await createChargeOffSessionFn({
                stripeCustomerId,
                amount,
                currency,
                paymentMethodId,
                returnUrl
            });
            const data = response.data as OrderPaymentIntent;
            console.log(data);
            return data;
        } catch (error: unknown) {
            console.log(error);
            return null;
        }
    }

    async acceptBatchOrder(batchId: string, shopperId: string, shopperName: string, shopperPhone: string, acceptedLocation: {latitude: number, longitude: number}) {
        const acceptBatchOrderFn = httpsCallable(this.firebaseFunctions, 'acceptBatchOrder');
        try {
            const response = await acceptBatchOrderFn({batchId, shopperId, shopperName, shopperPhone, acceptedLocation});
            const data = response.data;
            console.log(data);
        } catch (error: unknown) {
            console.log(error);
        }
    }

    async getNearbyAvailableBatches(latitude: number, longitude: number, radiusInKm: number): Promise<{success: boolean, batches: Batch[], total: number, queryLocation: { lat: number, lng: number }, radius: number}> {
        const getNearbyAvailableBatchesFn = httpsCallable<{latitude: number, longitude: number, radiusInKm: number}>(this.firebaseFunctions, 'getAvailableBatchesByLocation_onCall');
        try {
            const response = await getNearbyAvailableBatchesFn({latitude, longitude, radiusInKm});
            const data = response.data as {success: boolean, batches: Batch[], total: number, queryLocation: { lat: number, lng: number }, radius: number};
            console.log(data);
            return data;
        } catch (error: unknown) {     
            console.log(error);
            return {success: false, batches: [], total: 0, queryLocation: { lat: latitude, lng: longitude }, radius: radiusInKm};
        }
    }

    async getShopperBatches(status: string, shopperId: string) {
        const getShopperBatchesFn = httpsCallable(this.firebaseFunctions, 'getShopperBatches');
        if(!['available', 'accepted', 'in_progress', 'completed'].includes(status)) {
            throw new Error('Invalid status');
        }
        try {
            const response = await getShopperBatchesFn({status, shopperId});
            const data = response.data;
            console.log(data);
        } catch (error: unknown) {
            console.log(error);
        }
    }

}